// Dashboard — scoutm8 home page.
// Layout ported from the scoutm8-design system (dashboard-app.jsx): hero
// greeting · new jobs today · to-dos & follow-ups · candidate status · news
// signals. Styled with the website design tokens (navy ink + ScoutM8 blue
// accent + Instrument Serif display), scoped locally to this page via <Root>.
//
// Data is REAL (no mock content), sourced from live endpoints:
//   • New jobs today  → GET /api/jobs?scope=mine&mineType=tracked&source=<saved>&posted=0
//                       (saved companies = user_settings.companies, which the
//                       server keeps limited to companies the user has)
//   • Candidate status→ GET /api/user-candidates/:id + /api/outreach/external-candidates
//                       + GET /api/me/candidate-state (per-job pipeline status)
//   • To-dos & follow-ups → user-managed, persisted in outreach-state.todos
//   • News signals    → GET /api/scraped-news?jobs=<saved companies> (matched
//                       on jobs_header; scoped to user_settings.companies)
// Panels degrade to friendly empty states when a source has no data yet.
//
// Fonts (Instrument Serif / Geist / Geist Mono) load globally in public/index.html.

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { fetchWithAuth } from "../utils/authFetch";
import { patchOutreachState } from "../lib/outreachStateService";
import { getCandidateState } from "../lib/candidateStateService";
import { formatLocationCountry } from "../utils/locationUtils";
import { COMPANY_TINTS } from "../theme/ThemeContext";
import Skeleton from "../components/Skeleton";
import {
  getCachedUserSettings,
  getUserSettings,
} from "../lib/userSettingsService";
import {
  ACTIVE_STATUSES,
  DEFAULT_STATUS,
  furthestStatus,
  WON_STATUSES,
  statusLabel,
  statusRank,
} from "../lib/pipeline";
import CompanyLogo from "../components/CompanyLogo";
import { useActivity, ActivityRow } from "../components/ActivityTray";
import { timeAgo } from "../utils/timeAgo";

/* ── Icons (subset ported from the design's icon set) ─────────────────── */
const I = ({ size = 18, stroke = 1.6, children, fill = "none", ...rest }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    {children}
  </svg>
);
const IBriefcase = (p) => (
  <I {...p}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M3 13h18" />
  </I>
);
const ISparkles = (p) => (
  <I {...p}>
    <path d="M12 3v3" /><path d="M12 18v3" /><path d="M5 12H2" /><path d="M22 12h-3" />
    <path d="m4.9 4.9 2.1 2.1" /><path d="m17 17 2.1 2.1" /><path d="M19.1 4.9 17 7" />
    <path d="M7 17l-2.1 2.1" /><circle cx="12" cy="12" r="3" />
  </I>
);
const IChevRight = (p) => <I {...p}><path d="m9 18 6-6-6-6" /></I>;
const ICheck = (p) => <I {...p}><path d="m20 6-11 11-5-5" /></I>;
const IBolt = (p) => <I {...p}><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" /></I>;
const ICheckCircle = (p) => (
  <I {...p}><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></I>
);
const IUsers2 = (p) => (
  <I {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </I>
);
const INewspaper = (p) => (
  <I {...p}>
    <path d="M4 5a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v13a2 2 0 0 0 2 2H6a2 2 0 0 1-2-2z" />
    <path d="M17 8h2a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2" />
    <path d="M8 8h5" /><path d="M8 12h5" /><path d="M8 16h3" />
  </I>
);
const IMail = (p) => (
  <I {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></I>
);
const IRefresh = (p) => (
  <I {...p}>
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" />
  </I>
);
const IList = (p) => (
  <I {...p}>
    <path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" />
    <circle cx="3.5" cy="6" r="1" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="18" r="1" fill="currentColor" stroke="none" />
  </I>
);
const IClose = (p) => <I {...p}><path d="m6 6 12 12" /><path d="M18 6 6 18" /></I>;
const IPlus = (p) => <I {...p}><path d="M12 5v14" /><path d="M5 12h14" /></I>;
const IBuilding = (p) => (
  <I {...p}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M9 21v-4h6v4" />
    <path d="M8 7h.01M12 7h.01M16 7h.01M8 11h.01M12 11h.01M16 11h.01" />
  </I>
);

/* ── Helpers ──────────────────────────────────────────────────────────── */
function firstCompany(source) {
  if (Array.isArray(source)) return String(source[0] || "").trim();
  const s = String(source || "").trim();
  return s.includes("|") ? s.split("|")[0].trim() : s;
}

function initialsFrom(str) {
  const parts = String(str || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "•";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Shared with the Jobs pages so one person has one colour app-wide.
const TONES = COMPANY_TINTS;
function toneFor(str) {
  const s = String(str || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return TONES[h % TONES.length];
}

// The candidate id used as the key in candidate-state.
function candId(raw) {
  const v = raw && raw._id != null ? raw._id : raw && raw.id;
  return v == null ? "" : String(v);
}

// The card is a summary, not the full 9-stage board (that lives on /outreach).
// Statuses roll up into four funnel steps; `rejected` sits outside the funnel
// and is reported separately in the card subtitle.
const FUNNEL = [
  { id: "sourced", label: "Sourced", color: "var(--ink-3)" },
  { id: "outreach", label: "Outreach", color: "var(--warn)" },
  { id: "active", label: "In process", color: "var(--accent)" },
  { id: "placed", label: "Placed", color: "var(--success)" },
];

// Which persisted statuses sit under each funnel step. `sourced` is null =
// everyone, including candidates that were never actioned.
const STAGE_MEMBERS = {
  sourced: null,
  outreach: ["outreach"],
  active: ACTIVE_STATUSES,
  placed: WON_STATUSES,
  rejected: ["rejected"],
};

// Max faces shown in the drill-down before deferring to the Outreach board.
const STAGE_PREVIEW_MAX = 8;

/* ── Small building blocks ────────────────────────────────────────────── */
function ActivitiesCard() {
  const { jobs, running } = useActivity();
  const [showAll, setShowAll] = useState(false);
  const list = showAll ? jobs : jobs.slice(0, 6);
  return (
    <div className="dash-row last-row">
      <div className="card">
        <div className="card-head">
          <div>
            <h3 className="card-h serif"><IBolt size={15} className="head-ic" /> Activities</h3>
            <div className="card-sub">
              {running > 0 ? <><b className="num">{running}</b> running</> : "Nothing running"} · last 24 hours
            </div>
          </div>
          {jobs.length > 6 && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "Show fewer" : `Show all ${jobs.length}`}
            </button>
          )}
        </div>
        {jobs.length === 0 ? (
          <div className="card-sub" style={{ paddingTop: 8 }}>
            Imports, company tracking, matching, email lookups and searches will show here while they run.
          </div>
        ) : (
          <div>{list.map((j) => <ActivityRow key={j.id} job={j} />)}</div>
        )}
      </div>
    </div>
  );
}

function Logo({ color, text, size = 36, name }) {
  return <CompanyLogo name={name} size={size} radius={9} bg={color} fallback={text} className="co-logo" />;
}

function JobRow({ job, onOpen }) {
  const company = firstCompany(job.source) || "Company";
  const title = job.title || job.job_title || "Untitled role";
  const location = formatLocationCountry(job) || job.country || "";
  return (
    <button className="job-row" onClick={onOpen} title={job.function ? `${job.function} — open job` : "Open job"}>
      <Logo name={company} color={toneFor(company)} text={initialsFrom(company)} />
      <div className="job-main">
        <div className="job-role">{title}</div>
        <div className="job-meta">
          <span className="job-co">{company}</span>
          {location ? <><span className="dot" />{location}</> : null}
        </div>
      </div>
      <span className="job-posted">{timeAgo(job.posted_date)}</span>
    </button>
  );
}

function TodoRow({ todo, onToggle, onDelete }) {
  const iconMap = { reply: <IMail size={13} />, follow: <IRefresh size={13} />, todo: <IList size={13} /> };
  return (
    <div className={`todo-row ${todo.done ? "is-done" : ""}`}>
      <button
        className={`todo-check ${todo.done ? "checked" : ""}`}
        onClick={() => onToggle(todo.id)}
        aria-label={todo.done ? "Mark as not done" : "Mark as done"}
      >
        {todo.done && <ICheck size={12} />}
      </button>
      <div className={`todo-icon prio-${todo.priority || "med"}`}>{iconMap[todo.kind] || iconMap.todo}</div>
      <div className="todo-main">
        <div className="todo-text">{todo.text}</div>
        {todo.sub ? <div className="todo-sub">{todo.sub}</div> : null}
      </div>
      {todo.due ? (
        <span className={`todo-due due-${todo.due === "Today" ? "today" : "later"}`}>{todo.due}</span>
      ) : <span />}
      <button className="todo-del" onClick={() => onDelete(todo.id)} aria-label="Delete task" title="Delete">
        <IClose size={13} />
      </button>
    </div>
  );
}

function NewsRow({ item, onOpen }) {
  const company = item.jobs_header || item.source || "News";
  const publisher = item.source || "";
  return (
    <button className="news-row" onClick={onOpen} title={publisher ? `${publisher} — open article` : "Open article"}>
      <Logo name={company} color={toneFor(company)} text={initialsFrom(company)} size={38} />
      <div className="news-main">
        <div className="news-top">
          <span className="news-co">{company}</span>
          {item.published ? <><span className="dot" /><span className="news-when">{timeAgo(item.published)}</span></> : null}
        </div>
        <div className="news-head">{item.title || "Untitled"}</div>
      </div>
    </button>
  );
}

function EmptyState({ icon, title, text, cta, onCta }) {
  return (
    <div className="empty-state">
      <div className="empty-ico">{icon}</div>
      <div className="empty-title serif">{title}</div>
      {text ? <div className="empty-text">{text}</div> : null}
      {cta ? (
        <button className="btn btn-primary btn-sm empty-cta" onClick={onCta}>{cta}</button>
      ) : null}
    </div>
  );
}

// Last measured height of a card body holding real data, per user and card, so the
// next visit's skeleton takes exactly the room the content will.
function useRememberedHeight(key, ready) {
  const ref = useRef(null);
  const [height] = useState(() => {
    try { const v = Number(localStorage.getItem(key)); return v > 0 ? v : null; } catch { return null; }
  });
  useEffect(() => {
    const el = ref.current;
    if (!ready || !el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) { try { localStorage.setItem(key, String(h)); } catch { /* storage blocked */ } }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [key, ready]);
  return [ref, height];
}

// Skeleton row heights, pinned in CSS (.is-sk) to the real rows' rendered size.
const JOB_ROW_H = 66;
const TODO_ROW_H = 56;
const NEWS_ROW_H = 73;
const CAND_CARD_H = 87;
const PIPE_H = 135;

// Rows shaped like the real ones; as many as fit the remembered body height, else `fallback`.
function SkRows({ Row, rowH, gap = 0, height, fallback, max = 12, listClass }) {
  const n = height ? Math.min(max, Math.max(1, Math.floor((height + gap) / (rowH + gap)))) : fallback;
  return (
    <div className={`sk-body ${listClass}`} style={height ? { height } : undefined} aria-hidden="true">
      {Array.from({ length: n }, (_, i) => <Row key={i} />)}
    </div>
  );
}

function JobRowSk() {
  return (
    <div className="job-row is-sk">
      <Skeleton width={36} height={36} radius="9px" />
      <div className="job-main">
        <Skeleton width="58%" height={14} mb={7} />
        <Skeleton width="36%" height={11} />
      </div>
      <Skeleton width={40} height={11} />
    </div>
  );
}

function TodoRowSk() {
  return (
    <div className="todo-row is-sk">
      <Skeleton width={20} height={20} radius="6px" />
      <Skeleton width={28} height={28} radius="8px" />
      <div className="todo-main">
        <Skeleton width="64%" height={13} mb={6} />
        <Skeleton width="40%" height={11} />
      </div>
      <Skeleton width={46} height={19} radius="6px" />
      <span />
    </div>
  );
}

function NewsRowSk() {
  return (
    <div className="news-row is-sk">
      <Skeleton width={38} height={38} radius="9px" />
      <div className="news-main">
        <div className="news-top">
          <Skeleton width={120} height={12} />
        </div>
        <Skeleton width="72%" height={14} />
      </div>
    </div>
  );
}

// Funnel steps plus as many movement cards as the remembered height leaves room for.
function PipelineSk({ height }) {
  const rest = height == null ? null : height - PIPE_H - 25;
  const cards = rest == null ? 4 : Math.min(4, Math.max(0, Math.floor((rest + 10) / (CAND_CARD_H + 10))) * 2);
  return (
    <div className="sk-body" style={height ? { height } : undefined} aria-hidden="true">
      <div className="pipeline">
        {FUNNEL.map((f, i) => (
          <React.Fragment key={f.id}>
            <div className="pipe-stage is-sk">
              <Skeleton width={40} height={34} radius="7px" />
              <Skeleton width="100%" height={6} radius="99px" />
              <Skeleton width={56} height={12} />
            </div>
            {i < FUNNEL.length - 1 && <div className="pipe-arrow"><IChevRight size={13} /></div>}
          </React.Fragment>
        ))}
      </div>
      {cards > 0 ? (
        <>
          <div className="cand-sub-h"><Skeleton width={120} height={13} /></div>
          <div className="cand-grid">
            {Array.from({ length: cards }, (_, i) => (
              <div className="cand-card is-sk" key={i}>
                <Skeleton width={40} height={40} radius="10px" />
                <div className="cand-main">
                  <Skeleton width="60%" height={14} mb={5} />
                  <Skeleton width="80%" height={11} mb={9} />
                  <Skeleton width={64} height={19} radius="5px" />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────── */
function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [settings, setSettings] = useState(() =>
    user?.sub ? getCachedUserSettings(user.sub) : null,
  );
  const [settingsLoaded, setSettingsLoaded] = useState(
    () => !!(user?.sub && getCachedUserSettings(user.sub)),
  );
  const [jobs, setJobs] = useState({ list: [], total: 0, loading: true, noCompanies: false });
  const [news, setNews] = useState({ list: [], loading: true, noCompanies: false });
  const [pipe, setPipe] = useState({ stages: null, movement: [], people: [], rejected: 0, loading: true });
  // Which funnel step the candidate card is drilled into; null = recent movement.
  const [selectedStage, setSelectedStage] = useState(null);
  const [todos, setTodos] = useState([]);
  const [todosLoading, setTodosLoading] = useState(true);

  // Card bodies are measured only once their whole grid row has loaded, so a
  // sibling's skeleton never inflates the stored height.
  const mainReady = !jobs.loading && !todosLoading;
  const bottomReady = !pipe.loading && !news.loading;
  const skKey = (card) => `dash-sk:${user?.sub || "anon"}:${card}`;
  const [jobsRef, jobsH] = useRememberedHeight(skKey("jobs"), mainReady);
  const [todosRef, todosH] = useRememberedHeight(skKey("todos"), mainReady);
  const [pipeRef, pipeH] = useRememberedHeight(skKey("pipe"), bottomReady && !selectedStage);
  const [newsRef, newsH] = useRememberedHeight(skKey("news"), bottomReady);
  const [draft, setDraft] = useState("");
  const [draftKind, setDraftKind] = useState("todo");
  const [draftDue, setDraftDue] = useState("Today");
  const [modalOpen, setModalOpen] = useState(false);

  // Settings drive the greeting name AND the saved-companies list.
  useEffect(() => {
    if (!user?.sub) { setSettingsLoaded(true); return undefined; }
    let alive = true;
    getUserSettings(user.sub)
      .then((s) => { if (alive) { setSettings(s); setSettingsLoaded(true); } })
      .catch(() => { if (alive) setSettingsLoaded(true); });
    return () => { alive = false; };
  }, [user?.sub]);

  // Saved companies (user_settings.companies). The server only returns ones
  // the user actually tracks (scout-tracked + industry picks; never imported-job companies).
  const savedCompanyNames = useMemo(() => {
    const raw = settings?.companies;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((c) => (typeof c === "string" ? c : String(c?.name || "")).trim())
      .filter(Boolean);
  }, [settings]);
  const companiesKey = savedCompanyNames.join("|");
  const jobsHref = `/jobs?source=${encodeURIComponent(companiesKey)}&posted=0`;

  // New jobs posted today from the saved companies, read from tracked jobs.
  useEffect(() => {
    if (!settingsLoaded) return undefined;
    if (savedCompanyNames.length === 0) {
      setJobs({ list: [], total: 0, loading: false, noCompanies: true });
      return undefined;
    }
    let alive = true;
    setJobs((j) => ({ ...j, loading: true }));
    fetchWithAuth(`/api/jobs?scope=mine&mineType=tracked&source=${encodeURIComponent(companiesKey)}&posted=0&limit=10&includeTotal=true`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!alive) return;
        const all = Array.isArray(data) ? data : data.jobs || [];
        all.sort((a, b) => new Date(b.posted_date || 0) - new Date(a.posted_date || 0));
        const total = Array.isArray(data) ? all.length : data.total ?? all.length;
        // Card shows only the newest 5; the rest live behind the "see all" link.
        setJobs({ list: all.slice(0, 5), total, loading: false, noCompanies: false });
      })
      .catch(() => alive && setJobs({ list: [], total: 0, loading: false, noCompanies: false }));
    return () => { alive = false; };
  }, [settingsLoaded, companiesKey]);

  // News signals — from the saved companies (matched on the news item's
  // jobs_header via the pipe-delimited `jobs` filter).
  useEffect(() => {
    if (!settingsLoaded) return undefined;
    if (savedCompanyNames.length === 0) {
      setNews({ list: [], loading: false, noCompanies: true });
      return undefined;
    }
    let alive = true;
    setNews((n) => ({ ...n, loading: true }));
    fetchWithAuth(`/api/scraped-news?jobs=${encodeURIComponent(companiesKey)}&limit=5`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!alive) return;
        setNews({ list: Array.isArray(data) ? data : [], loading: false, noCompanies: false });
      })
      .catch(() => alive && setNews({ list: [], loading: false, noCompanies: false }));
    return () => { alive = false; };
  }, [settingsLoaded, companiesKey]);

  // Candidate pipeline + follow-ups: join candidate lists with pipeline status.
  useEffect(() => {
    if (!user?.sub) return undefined;
    let alive = true;

    Promise.allSettled([
      getCandidateState(),
      fetchWithAuth("/api/outreach/external-candidates").then((r) => (r.ok ? r.json() : { candidates: [] })),
      fetchWithAuth(`/api/user-candidates/${encodeURIComponent(user.sub)}?limit=100`).then((r) => (r.ok ? r.json() : { candidates: [], total: 0 })),
    ]).then(([stateRes, extRes, intRes]) => {
      if (!alive) return;

      // One row per candidate: { [candidateId]: { pool, jobs } }.
      const statuses = stateRes.status === "fulfilled" ? stateRes.value || {} : {};
      const external = extRes.status === "fulfilled" ? extRes.value?.candidates || [] : [];
      const internal = intRes.status === "fulfilled" ? intRes.value?.candidates || [] : [];
      const internalTotal = intRes.status === "fulfilled" ? intRes.value?.total ?? internal.length : internal.length;

      // Index candidates so we can name status entries.
      const byId = new Map();
      const addCand = (raw, origin) => {
        const id = candId(raw);
        if (!id || byId.has(id)) return;
        const name = raw.name || "—";
        const role = raw.position || raw.role || "";
        const company = raw.company || "";
        byId.set(id, { id, name, role, company, origin, initials: initialsFrom(name), tone: toneFor(name) });
      };
      external.forEach((c) => addCand(c, "external"));
      internal.forEach((c) => addCand(c, "internal"));

      // Each candidate row holds a status per job, so someone on three jobs has
      // three. Collapse to one per person — furthest along — otherwise the
      // funnel counts pipelines and reads as more candidates than exist.
      let outreach = 0;
      let active = 0;
      let placed = 0;
      let rejected = 0;
      Object.keys(statuses).forEach((personId) => {
        const s = furthestStatus(statuses, personId).status;
        if (s === "outreach") outreach += 1;
        else if (ACTIVE_STATUSES.includes(s)) active += 1;
        else if (WON_STATUSES.includes(s)) placed += 1;
        else if (s === "rejected") rejected += 1;
      });
      // Sourced is the whole candidate base, not a status bucket — candidates
      // with no status entry are implicitly `target`.
      const counts = {
        sourced: internalTotal + external.length,
        outreach,
        active,
        placed,
      };
      const stages = FUNNEL.map((f) => ({ ...f, count: counts[f.id] }));

      // Recent movement: anyone actioned past the default, furthest along first.
      // `rejected` is an exit, not progress, so it sorts last despite its index.
      const progress = (s) => (s === "rejected" ? -1 : statusRank(s));
      const actioned = Object.keys(statuses)
        .map((id) => [id, furthestStatus(statuses, id).status])
        .filter(([id, s]) => byId.has(id) && s !== DEFAULT_STATUS)
        .sort((a, b) => progress(b[1]) - progress(a[1]))
        .slice(0, 4)
        .map(([id, s]) => ({ ...byId.get(id), status: s }));
      // Nothing actioned yet → show the newest candidates at the default stage.
      const movement = actioned.length
        ? actioned
        : Array.from(byId.values()).slice(0, 4).map((c) => ({ ...c, status: DEFAULT_STATUS }));

      // Every candidate with its status resolved, ordered furthest-along first.
      // Backs the per-stage drill-down; no status entry means `target`.
      const people = Array.from(byId.values())
        .map((c) => ({ ...c, status: furthestStatus(statuses, c.id).status }))
        .sort((a, b) => progress(b.status) - progress(a.status));

      setPipe({ stages, movement, people, rejected, loading: false });

      // User-managed to-dos & follow-ups, persisted in the client DB via the
      // encrypted outreach-state doc (state.todos).
      const savedTodos =
        stateRes.status === "fulfilled" && Array.isArray(stateRes.value?.todos)
          ? stateRes.value.todos
          : [];
      setTodos(savedTodos);
      setTodosLoading(false);
    });

    return () => { alive = false; };
  }, [user?.sub]);

  // Close the add-task modal on Escape.
  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setModalOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen]);

  // Persist the full to-do list to the client DB (server replaces the array).
  const persistTodos = (next) =>
    patchOutreachState({ todos: next }).catch((e) =>
      console.error("Failed to save to-dos:", e?.message || e),
    );

  const genId = () =>
    (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
    `t-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const openAddModal = () => {
    setDraft("");
    setDraftKind("todo");
    setDraftDue("Today");
    setModalOpen(true);
  };

  const addTodo = () => {
    const text = draft.trim();
    if (!text) return;
    const item = {
      id: genId(),
      text,
      kind: draftKind,
      due: draftDue,
      priority: draftKind === "reply" ? "high" : "med",
      done: false,
      createdAt: new Date().toISOString(),
    };
    const next = [item, ...todos];
    setTodos(next);
    persistTodos(next);
    setDraft("");
    setModalOpen(false);
  };

  const toggleTodo = (id) => {
    const next = todos.map((t) => (t.id === id ? { ...t, done: !t.done } : t));
    setTodos(next);
    persistTodos(next);
  };

  const deleteTodo = (id) => {
    const next = todos.filter((t) => t.id !== id);
    setTodos(next);
    persistTodos(next);
  };

  const openTodos = todos.filter((t) => !t.done);
  const dueToday = openTodos.filter((t) => t.due === "Today").length;
  const totalCandidates = pipe.stages ? pipe.stages[0].count : 0;
  const pipelineMax = pipe.stages ? Math.max(1, ...pipe.stages.map((p) => p.count)) : 1;

  // Drill-down: members of the selected step, else the default recent-movement set.
  const stageMembers = useMemo(() => {
    if (!selectedStage) return pipe.movement;
    const allowed = STAGE_MEMBERS[selectedStage];
    const people = pipe.people || [];
    return allowed ? people.filter((p) => allowed.includes(p.status)) : people;
  }, [selectedStage, pipe.movement, pipe.people]);

  // Authoritative count for the step. `sourced` and `rejected` are counted from
  // totals rather than the fetched page, so they can exceed what we can list.
  const stageTotal =
    selectedStage === "sourced"
      ? totalCandidates
      : selectedStage === "rejected"
        ? pipe.rejected
        : pipe.stages?.find((s) => s.id === selectedStage)?.count ?? stageMembers.length;

  const stageLabel =
    selectedStage === "rejected"
      ? "Rejected"
      : FUNNEL.find((f) => f.id === selectedStage)?.label || "";

  const toggleStage = (id) =>
    setSelectedStage((cur) => (cur === id ? null : id));

  const now = new Date();
  const hour = now.getHours();
  const partOfDay = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const firstName = String(settings?.firstName || "").trim() || "there";
  const dateLabel = now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const timeLabel = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });

  return (
    <Root className="dash-root">
      {/* Hero greeting */}
      <div className="dash-hero">
        <div>
          <div className="dash-eyebrow">
            <span className="pulse-dot" />
            {dateLabel} · {timeLabel}
          </div>
          <h1 className="dash-greeting serif">
            Good {partOfDay}, <em>{firstName}.</em>
          </h1>
        </div>
        <div className="dash-hero-actions">
          <button className="btn btn-lg" onClick={() => navigate("/jobs")}>
            <IBriefcase size={14} /> Browse jobs
          </button>
          <button className="btn btn-primary btn-lg" onClick={() => navigate("/find-candidates")}>
            <ISparkles size={15} /> New candidate search
          </button>
        </div>
      </div>

      {/* Row: New jobs today + To-dos */}
      <div className="dash-row main-row">
        <div className="card">
          <div className="card-head">
            <div>
              <h3 className="card-h serif"><IBolt size={15} className="head-ic" /> New jobs today</h3>
              <div className="card-sub">{jobs.total > 0 ? <><b className="num">{jobs.total}</b> from saved companies</> : "From saved companies"}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate(jobsHref)}>See all <IChevRight size={11} /></button>
          </div>
          {jobs.loading ? (
            <SkRows Row={JobRowSk} rowH={JOB_ROW_H} height={jobsH} fallback={4} max={6} listClass="job-list" />
          ) : (
            <div ref={jobsRef} className="card-body">
              {jobs.noCompanies ? (
                <EmptyState
                  icon={<IBuilding size={24} />}
                  title="No saved companies yet"
                  text="Save companies in Research and their new roles land here each day."
                  cta="Save companies"
                  onCta={() => navigate("/research")}
                />
              ) : jobs.list.length === 0 ? (
                <EmptyState
                  icon={<IBriefcase size={24} />}
                  title="All quiet today"
                  text="Nothing new from your saved companies yet."
                  cta="Open tracked jobs"
                  onCta={() => navigate("/jobs")}
                />
              ) : (
                <>
                  <div className="job-list">
                    {jobs.list.map((j) => (
                      <JobRow key={j._id || j.id} job={j} onOpen={() => navigate(jobsHref)} />
                    ))}
                  </div>
                  {jobs.total > jobs.list.length ? (
                    <button className="row-more" onClick={() => navigate(jobsHref)}>
                      + {jobs.total - jobs.list.length} more job{jobs.total - jobs.list.length === 1 ? "" : "s"} today
                    </button>
                  ) : null}
                </>
              )}
            </div>
          )}
        </div>

        <div className="card todo-card">
          <div className="card-head">
            <div>
              <h3 className="card-h serif"><ICheckCircle size={15} className="head-ic" /> To-dos</h3>
              <div className="card-sub">{dueToday > 0 ? <><b className="accent-text num">{dueToday}</b> due today</> : <><b className="num">{openTodos.length}</b> open</>}</div>
            </div>
            <button className="btn btn-sm" onClick={openAddModal}><IPlus size={13} /> Add</button>
          </div>

          {todosLoading ? (
            <SkRows Row={TodoRowSk} rowH={TODO_ROW_H} gap={2} height={todosH} fallback={3} listClass="todo-list" />
          ) : (
            <div ref={todosRef} className="card-body">
              {todos.length === 0 ? (
                <EmptyState
                  icon={<ICheckCircle size={24} />}
                  title="Your list is clear"
                  text="Tasks and follow-ups you add show up here."
                  cta="Add a task"
                  onCta={openAddModal}
                />
              ) : (
                <div className="todo-list">
                  {todos.map((t) => <TodoRow key={t.id} todo={t} onToggle={toggleTodo} onDelete={deleteTodo} />)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Candidate status + News signals */}
      <div className="dash-row bottom-row">
        <div className="card">
          <div className="card-head">
            <div>
              <h3 className="card-h serif"><IUsers2 size={15} className="head-ic" /> Pipeline</h3>
              <div className="card-sub">
                <b className="num">{totalCandidates}</b> candidate{totalCandidates === 1 ? "" : "s"}
                {pipe.rejected > 0 ? (
                  <> · <button type="button" className="link-btn sub-link" onClick={() => toggleStage("rejected")}>
                    <b className="num">{pipe.rejected}</b> rejected
                  </button></>
                ) : null}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/outreach")}>See all <IChevRight size={11} /></button>
          </div>

          {pipe.loading ? (
            <PipelineSk height={pipeH} />
          ) : (
            <div ref={pipeRef} className="card-body">
              {totalCandidates === 0 ? (
                <EmptyState
                  icon={<IUsers2 size={24} />}
                  title="No candidates yet"
                  text="Find candidates to start your pipeline."
                  cta="Find candidates"
                  onCta={() => navigate("/find-candidates")}
                />
              ) : (
                <>
                  <div className="pipeline">
                    {pipe.stages.map((s, i) => (
                      <React.Fragment key={s.id}>
                        <button
                          type="button"
                          className={`pipe-stage ${selectedStage === s.id ? "is-selected" : ""}`}
                          aria-pressed={selectedStage === s.id}
                          aria-label={`${s.count} ${s.label} — show candidates`}
                          onClick={() => toggleStage(s.id)}
                        >
                          <div className="pipe-count serif" style={{ color: s.color }}>{s.count}</div>
                          <div className="pipe-bar"><div className="pipe-bar-fill" style={{ width: `${(s.count / pipelineMax) * 100}%`, background: s.color }} /></div>
                          <div className="pipe-label">{s.label}</div>
                        </button>
                        {i < pipe.stages.length - 1 && <div className="pipe-arrow"><IChevRight size={13} /></div>}
                      </React.Fragment>
                    ))}
                  </div>

                  {(selectedStage || pipe.movement.length > 0) && (
                    <>
                      <div className="cand-sub-h">
                        <span>
                          {selectedStage ? `${stageLabel} · ${stageTotal}` : "Recent movement"}
                        </span>
                        {selectedStage ? (
                          <button type="button" className="link-btn stage-reset" onClick={() => setSelectedStage(null)}>
                            Show recent
                          </button>
                        ) : null}
                      </div>
                      {stageMembers.length === 0 ? (
                        <div className="empty-note">No candidates at this stage yet.</div>
                      ) : (
                      <div className="cand-grid">
                        {stageMembers.slice(0, selectedStage ? STAGE_PREVIEW_MAX : 4).map((c) => {
                          return (
                            <button
                              key={c.id}
                              className="cand-card"
                              title={`Open ${c.name}`}
                              // Deep-link straight to this person's profile on the
                              // Outreach board (same params as the job Candidates tab).
                              onClick={() => navigate(`/outreach?tab=candidates&personId=${encodeURIComponent(c.id)}`)}
                            >
                              <span className="cand-avatar" style={{ background: c.tone }}>{c.initials}</span>
                              <div className="cand-main">
                                <div className="cand-name">{c.name}</div>
                                <div className="cand-role">{[c.role, c.company].filter(Boolean).join(" · ") || "Candidate"}</div>
                                <div className="cand-foot">
                                  <span className={`stage-pill stage-${c.status}`}>{statusLabel(c.status)}</span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      )}
                      {selectedStage && stageTotal > Math.min(stageMembers.length, STAGE_PREVIEW_MAX) ? (
                        <button className="row-more" onClick={() => navigate("/outreach")}>
                          See all {stageTotal} in {stageLabel}
                        </button>
                      ) : null}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <h3 className="card-h serif"><INewspaper size={15} className="head-ic" /> News</h3>
              <div className="card-sub">From saved companies</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/research")}>See all <IChevRight size={11} /></button>
          </div>
          {news.loading ? (
            <SkRows Row={NewsRowSk} rowH={NEWS_ROW_H} height={newsH} fallback={4} max={6} listClass="news-list" />
          ) : (
            <div ref={newsRef} className="card-body">
              {news.noCompanies ? (
                <EmptyState
                  icon={<IBuilding size={24} />}
                  title="No saved companies yet"
                  text="Save companies in Research to see their news here."
                  cta="Save companies"
                  onCta={() => navigate("/research")}
                />
              ) : news.list.length === 0 ? (
                <EmptyState
                  icon={<INewspaper size={24} />}
                  title="No news yet"
                  text="News from your saved companies lands here."
                  cta="Open research"
                  onCta={() => navigate("/research")}
                />
              ) : (
                <div className="news-list">
                  {news.list.slice(0, 5).map((n) => (
                    <NewsRow
                      key={n._id || n.original_url}
                      item={n}
                      onOpen={() => (n.original_url ? window.open(n.original_url, "_blank", "noopener,noreferrer") : navigate("/research"))}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Activities: everything running, plus the last day's results */}
      <ActivitiesCard />

      {/* Add task / follow-up modal */}
      {modalOpen && (
        <div className="dash-modal-backdrop" onClick={() => setModalOpen(false)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-label="Add a task" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-head">
              <h3 className="serif">Add a task</h3>
              <p>Add a to-do or follow-up to your list.</p>
            </div>
            <div className="dash-modal-body">
              <div className="dash-field">
                <label htmlFor="todo-text">Task</label>
                <input
                  id="todo-text"
                  className="t-input"
                  autoFocus
                  placeholder="e.g. Follow up with Priya about the PM role"
                  value={draft}
                  maxLength={200}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addTodo(); }}
                />
              </div>
              <div className="dash-field-row">
                <div className="dash-field">
                  <label htmlFor="todo-type">Type</label>
                  <select id="todo-type" className="t-select" value={draftKind} onChange={(e) => setDraftKind(e.target.value)}>
                    <option value="todo">Task</option>
                    <option value="follow">Follow-up</option>
                    <option value="reply">Reply</option>
                  </select>
                </div>
                <div className="dash-field">
                  <label htmlFor="todo-due">Due</label>
                  <select id="todo-due" className="t-select" value={draftDue} onChange={(e) => setDraftDue(e.target.value)}>
                    <option value="Today">Today</option>
                    <option value="Tomorrow">Tomorrow</option>
                    <option value="This week">This week</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="dash-modal-foot">
              <button className="btn" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addTodo} disabled={!draft.trim()}>Add task</button>
            </div>
          </div>
        </div>
      )}
    </Root>
  );
}

export default Dashboard;

/* ── Styles: website design tokens, scoped to this page ───────────────── */
/* Light and dark are two full token sets rather than one set with overrides —
   every value below has a counterpart, so nothing can silently stay light. */
/* Every colour here clears WCAG AA (4.5:1) against the surface it sits on —
   the brand blue itself only manages 2.5:1 on these surfaces, so the accent is
   lifted along its own hue instead of being swapped for a different blue. */
const Root = styled.div`
  /* Type */
  --font-display: 'Instrument Serif', 'New York', Georgia, serif;
  --font-body: 'Geist', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
  --font-mono: 'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace;

  /* Break out of MainContent's padding so the design fills the panel, then
     paint the website background. Sides/bottom bleed; top clearance is kept on
     mobile for the fixed menu button. */
  margin: -2.5rem;
  min-height: 100dvh;
  background: var(--bg);
  color: var(--ink);
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
  font-feature-settings: "ss01", "cv11";
  overflow-x: hidden;

  @media (max-width: 1024px) { margin: 0 -1.5rem -1.5rem -1.5rem; }
  @media (max-width: 768px)  { margin: 0 -1rem -1rem -1rem; }
  @media (max-width: 450px)  { margin: 0 -0.75rem -0.75rem -0.75rem; }
  @media (max-width: 390px)  { margin: 0 -0.5rem -0.5rem -0.5rem; }

  * { box-sizing: border-box; }
  .serif { font-family: var(--font-display); font-weight: 400; letter-spacing: -.005em; }
  .num { font-variant-numeric: tabular-nums; }
  .accent-text { color: var(--accent); }

  /* Buttons (base from styles.css) */
  .btn {
    display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 12px;
    border-radius: 8px; border: 1px solid var(--line); background: var(--surface); color: var(--ink);
    font: inherit; font-weight: 500; font-size: 13px; cursor: pointer; white-space: nowrap;
    transition: background .12s, border-color .12s, color .12s, transform .06s;
  }
  .btn:hover { background: var(--surface-tint); border-color: var(--line-strong); }
  .btn:active { transform: translateY(.5px); }
  .btn-primary { background: var(--accent); color: var(--on-fill); border-color: var(--accent); }
  .btn-primary:hover { background: var(--accent-hover); border-color: var(--accent-hover); }
  .btn-ghost { background: transparent; border-color: transparent; }
  .btn-ghost:hover { background: var(--bg-2); }
  .btn-sm { height: 28px; padding: 0 10px; font-size: 12.5px; border-radius: 7px; }
  .btn-lg { height: 38px; padding: 0 16px; font-size: 14px; border-radius: 10px; }
  .link-btn { background: none; border: 0; color: var(--accent); font: inherit; font-weight: 600; cursor: pointer; padding: 0; }
  .link-btn:hover { text-decoration: underline; }

  /* Generic card */
  .card { background: var(--surface); border: 1px solid var(--line); border-radius: 14px; padding: 20px 22px; }
  .card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 18px; }
  .card-h { font-family: var(--font-display); font-size: 23px; line-height: 1.1; color: var(--ink); margin: 0; letter-spacing: -.005em; display: flex; align-items: center; gap: 9px; }
  .head-ic { color: var(--accent); flex-shrink: 0; }
  .card-sub { font-size: 12.5px; color: var(--ink-muted); margin-top: 5px; }
  .card-sub b { color: var(--ink); font-weight: 600; }
  .card-sub b.accent-text { color: var(--accent); }
  .dot { display: inline-block; width: 3px; height: 3px; border-radius: 99px; background: var(--ink-faint); margin: 0 7px; vertical-align: middle; flex-shrink: 0; }
  .empty-note { font-size: 13px; color: var(--ink-muted); padding: 16px 4px 8px; line-height: 1.5; }

  /* Empty states — icon tile + serif title + copy + CTA */
  .empty-state { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 30px 20px 26px; margin: auto 0; }
  .empty-ico {
    width: 54px; height: 54px; border-radius: 15px; display: grid; place-items: center;
    color: var(--accent);
    background: linear-gradient(155deg, var(--accent-soft), var(--surface-tint));
    border: 1px solid var(--line); margin-bottom: 14px;
    box-shadow: 0 6px 16px -8px var(--accent-ring);
  }
  .empty-title { font-family: var(--font-display); font-size: 20px; line-height: 1.15; color: var(--ink); letter-spacing: -.005em; }
  .empty-text { font-size: 12.5px; color: var(--ink-muted); max-width: 280px; line-height: 1.5; margin-top: 5px; }
  .empty-cta { margin-top: 15px; }

  /* Skeletons — real row classes with shimmer blocks inside, cut to the remembered body height */
  .card-body { min-width: 0; }
  .todo-card .card-body, .bottom-row .card-body { flex: 1; display: flex; flex-direction: column; }
  .sk-body { overflow: hidden; }
  .bottom-row .card .sk-body { flex: 0 0 auto; }
  .is-sk { pointer-events: none; box-sizing: border-box; }
  .is-sk .job-main, .is-sk .news-main, .is-sk .cand-main, .is-sk .todo-main { display: flex; flex-direction: column; justify-content: center; }
  .job-row.is-sk { height: 66px; }
  .todo-row.is-sk { height: 56px; }
  .news-row.is-sk { height: 73px; }
  .cand-card.is-sk { height: 87px; }

  /* Hero */
  .dash-hero { max-width: 1360px; margin: 0 auto; padding: 40px 40px 30px; display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; }
  /* Greeting column must be able to shrink — the actions beside it are nowrap. */
  .dash-hero > div:first-child { min-width: 0; }
  .dash-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; color: var(--ink-muted); font-weight: 500; margin-bottom: 12px; }
  .pulse-dot { display: inline-block; width: 7px; height: 7px; border-radius: 99px; background: var(--accent); animation: dashPulse 1.4s ease-in-out infinite; }
  @keyframes dashPulse { 0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 50%, transparent); } 70% { box-shadow: 0 0 0 8px transparent; } 100% { box-shadow: 0 0 0 0 transparent; } }
  .dash-greeting { font-family: var(--font-display); font-size: clamp(34px, 5vw, 54px); line-height: 1.05; color: var(--ink); letter-spacing: -.015em; margin: 0; }
  .dash-greeting em { font-style: italic; color: var(--accent); }
  .dash-hero-actions { display: flex; gap: 8px; flex-shrink: 0; }

  /* Rows */
  .dash-row { max-width: 1360px; margin: 0 auto 16px; padding: 0 40px; }
  .dash-row.last-row { margin-bottom: 44px; }
  /* minmax(0, …) on every fr track: a bare 1fr floors at min-content, so a
     dense card would widen the row instead of the card scrolling/ellipsing. */
  .main-row { display: grid; grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr); gap: 16px; }
  .bottom-row { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr); gap: 16px; align-items: stretch; }
  .bottom-row .card { display: flex; flex-direction: column; }
  .bottom-row .news-list { flex: 1; }

  /* Company logo chip */
  .co-logo { border-radius: 9px; color: var(--on-fill); font-weight: 700; display: inline-grid; place-items: center; flex-shrink: 0; letter-spacing: -.01em; }

  /* Job list */
  .job-list { display: flex; flex-direction: column; }
  .job-row { display: flex; align-items: center; gap: 14px; width: 100%; text-align: left; font: inherit; color: inherit; background: none; border: 0; border-bottom: 1px solid var(--line-soft); padding: 14px 8px; margin: 0 -8px; cursor: pointer; transition: background .12s; border-radius: 8px; }
  .job-row:hover { background: var(--surface-tint); }
  .job-row:last-of-type { border-bottom: 0; }
  .job-main { flex: 1; min-width: 0; }
  .job-role { font-size: 14.5px; font-weight: 500; color: var(--ink); line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .job-meta { font-size: 12.5px; color: var(--ink-muted); margin-top: 3px; display: flex; align-items: center; min-width: 0; overflow: hidden; white-space: nowrap; }
  .job-co { color: var(--ink-soft); font-weight: 500; }
  .job-posted { font-size: 12.5px; color: var(--ink-muted); flex-shrink: 0; white-space: nowrap; }
  .row-more { width: 100%; margin-top: 8px; padding: 10px; background: var(--surface-tint); border: 1px dashed var(--line-strong); border-radius: 9px; color: var(--ink-soft); font: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer; transition: background .12s, color .12s; }
  .row-more:hover { background: var(--bg-2); color: var(--ink); }

  /* To-dos */
  .todo-card { display: flex; flex-direction: column; }
  .todo-list { display: flex; flex-direction: column; gap: 2px; }
  .todo-row { display: grid; grid-template-columns: 22px 28px minmax(0, 1fr) auto auto; align-items: center; gap: 11px; padding: 10px 6px; border-radius: 9px; transition: background .12s; }
  .todo-row:hover { background: var(--surface-tint); }
  .todo-del { width: 24px; height: 24px; border-radius: 6px; border: 0; background: transparent; color: var(--ink-faint); cursor: pointer; display: grid; place-items: center; opacity: 0; transition: opacity .12s, color .12s, background .12s; }
  .todo-row:hover .todo-del { opacity: 1; }
  .todo-del:hover { color: var(--danger); background: var(--danger-soft); }
  .btn:disabled { opacity: .5; cursor: not-allowed; }
  @media (hover: none) { .todo-del { opacity: 1; } }

  /* Form controls (used by the add-task modal) */
  .t-input { width: 100%; height: 38px; padding: 0 12px; border-radius: 8px; border: 1px solid var(--line); background: var(--surface); color: var(--ink); font: inherit; font-size: 14px; outline: none; transition: border-color .12s, box-shadow .12s; }
  .t-input::placeholder { color: var(--ink-muted); }
  .t-input:focus { border-color: var(--ink-3); box-shadow: 0 0 0 3px var(--accent-ring); }
  .t-select { width: 100%; height: 38px; border-radius: 8px; border: 1px solid var(--line); background: var(--surface); color: var(--ink); font: inherit; font-size: 13.5px; padding: 0 8px; cursor: pointer; outline: none; }
  .t-select:focus { border-color: var(--ink-3); box-shadow: 0 0 0 3px var(--accent-ring); }

  /* Add-task modal */
  .dash-modal-backdrop { position: fixed; inset: 0; z-index: 1000; background: var(--scrim); -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px); display: grid; place-items: center; padding: 24px; }
  .dash-modal { width: 100%; max-width: 440px; background: var(--surface); border: 1px solid var(--line); border-radius: 16px; box-shadow: var(--shadow-lg); overflow: hidden; }
  .dash-modal-head { padding: 18px 22px 12px; border-bottom: 1px solid var(--line-soft); }
  .dash-modal-head h3 { margin: 0; font-family: var(--font-display); font-size: 24px; line-height: 1.1; letter-spacing: -.005em; color: var(--ink); }
  .dash-modal-head p { margin: 3px 0 0; font-size: 12.5px; color: var(--ink-muted); }
  .dash-modal-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 14px; }
  .dash-field label { display: block; font-size: 12px; font-weight: 500; color: var(--ink-soft); margin-bottom: 6px; }
  .dash-field-row { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 12px; }
  .dash-modal-foot { padding: 14px 22px; border-top: 1px solid var(--line-soft); background: var(--surface-tint); display: flex; justify-content: flex-end; gap: 8px; }
  .todo-row.is-done .todo-text { text-decoration: line-through; color: var(--ink-muted); }
  .todo-row.is-done .todo-icon, .todo-row.is-done .todo-sub { opacity: .5; }
  .todo-check { width: 20px; height: 20px; border-radius: 6px; border: 1.6px solid var(--line-strong); background: var(--surface); cursor: pointer; display: grid; place-items: center; color: var(--on-fill); transition: background .12s, border-color .12s; padding: 0; }
  /* Same reason: --success lightens in dark mode, so the tick inverts too. */
  .todo-check.checked { background: var(--success); border-color: var(--success); color: var(--bg); }
  .todo-check:hover { border-color: var(--ink-soft); }
  .todo-icon { width: 28px; height: 28px; border-radius: 8px; display: grid; place-items: center; flex-shrink: 0; }
  .todo-icon.prio-high { background: var(--danger-soft); color: var(--danger); }
  .todo-icon.prio-med { background: var(--accent-soft); color: var(--accent-ink); }
  .todo-icon.prio-low { background: var(--bg-2); color: var(--ink-soft); }
  .todo-main { min-width: 0; }
  .todo-text { font-size: 13.5px; font-weight: 600; color: var(--ink); line-height: 1.25; }
  .todo-sub { font-size: 11.5px; color: var(--ink-muted); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .todo-due { font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 6px; white-space: nowrap; }
  .todo-due.due-today { color: var(--accent-ink); background: var(--accent-soft); }
  .todo-due.due-later { color: var(--ink-muted); background: var(--bg-2); }

  /* Pipeline */
  .pipeline { display: flex; align-items: stretch; gap: 4px; padding: 6px 0 20px; border-bottom: 1px solid var(--line-soft); margin-bottom: 18px; }
  /* Pressable: selecting a step drills the list below down to its members. */
  .pipe-stage { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: center; gap: 9px; background: none; border: 1px solid transparent; font: inherit; color: inherit; cursor: pointer; padding: 8px 6px; border-radius: 11px; transition: background .12s, border-color .12s; }
  .pipe-stage:hover { background: var(--surface-tint); border-color: var(--line-soft); }
  .pipe-stage:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
  .pipe-stage.is-selected { background: var(--surface-tint); border-color: var(--line-strong); }
  .pipe-count { font-size: 34px; line-height: 1; letter-spacing: -.01em; font-variant-numeric: tabular-nums; }
  .pipe-bar { width: 100%; height: 6px; background: var(--bg-2); border-radius: 99px; overflow: hidden; }
  .pipe-bar-fill { height: 100%; border-radius: 99px; }
  .pipe-label { font-size: 12px; color: var(--ink-soft); font-weight: 500; }
  .pipe-arrow { display: flex; align-items: center; color: var(--ink-faint); padding-top: 14px; }

  .cand-sub-h { font-size: 12.5px; font-weight: 500; color: var(--ink-muted); margin-bottom: 12px; display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
  .stage-reset { font-size: 11px; text-transform: none; letter-spacing: 0; flex-shrink: 0; }
  .sub-link { font: inherit; font-size: inherit; color: var(--ink-muted); font-weight: inherit; }
  .sub-link:hover { color: var(--accent); }
  .sub-link b { color: var(--ink); font-weight: 600; }
  .cand-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .cand-card { display: flex; align-items: center; gap: 12px; text-align: left; font: inherit; color: inherit; background: var(--surface-tint); border: 1px solid var(--line-soft); border-radius: 11px; padding: 13px 15px; cursor: pointer; transition: border-color .12s, box-shadow .12s; }
  .cand-card:hover { border-color: var(--line-strong); box-shadow: var(--shadow-sm); }
  .cand-avatar { width: 40px; height: 40px; border-radius: 10px; color: var(--on-fill); font-weight: 700; font-size: 14px; display: grid; place-items: center; flex-shrink: 0; }
  .cand-main { flex: 1; min-width: 0; }
  .cand-name { font-size: 14px; font-weight: 600; color: var(--ink); }
  .cand-role { font-size: 12px; color: var(--ink-muted); margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cand-foot { display: flex; align-items: center; gap: 8px; margin-top: 7px; }
  .stage-pill { font-size: 10.5px; font-weight: 600; padding: 2px 7px; border-radius: 5px; text-transform: uppercase; letter-spacing: .03em; }
  /* Grouped to match STATUS_COLORS on the Outreach page: target = neutral,
     outreach = awaiting, longlist→interviewing = active, offer/placement = won,
     rejected = lost. */
  .stage-pill.stage-target { color: var(--ink-soft); background: var(--bg-2); }
  .stage-pill.stage-outreach { color: var(--warn); background: var(--warn-soft); }
  .stage-pill.stage-longlist,
  .stage-pill.stage-shortlist,
  .stage-pill.stage-first_interview,
  .stage-pill.stage-interviewing { color: var(--accent-ink); background: var(--accent-soft); }
  .stage-pill.stage-offer,
  .stage-pill.stage-placement { color: var(--success); background: var(--success-soft); }
  .stage-pill.stage-rejected { color: var(--danger); background: var(--danger-soft); }
  /* Fit band from the search that sourced the person: High and above green, Medium accent, below warn. */

  /* News signals */
  .news-list { display: flex; flex-direction: column; }
  .news-row { display: flex; align-items: center; gap: 14px; width: 100%; text-align: left; font: inherit; color: inherit; background: none; border: 0; border-bottom: 1px solid var(--line-soft); padding: 16px 8px; margin: 0 -8px; cursor: pointer; transition: background .12s; border-radius: 8px; }
  .news-row:hover { background: var(--surface-tint); }
  .news-row:last-child { border-bottom: 0; }
  .news-main { flex: 1; min-width: 0; }
  /* Single line, always. When space runs out the publisher gives way first
     (shrink 3), then the company tag; the timestamp never shrinks. */
  .news-top { display: flex; align-items: center; margin-bottom: 4px; flex-wrap: nowrap; min-width: 0; }
  .news-co { font-size: 12.5px; color: var(--ink-soft); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
  .news-when { font-size: 12.5px; color: var(--ink-muted); white-space: nowrap; flex-shrink: 0; }
  .news-head { font-size: 14.5px; font-weight: 500; color: var(--ink); line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .news-note { font-size: 12px; color: var(--accent-ink); margin-top: 4px; display: flex; align-items: center; gap: 5px; }
  .news-note svg { color: var(--accent); flex-shrink: 0; }

  @media (max-width: 1080px) {
    .main-row, .bottom-row, .cand-grid { grid-template-columns: minmax(0, 1fr); }
    .dash-row { padding: 0 24px; }
    .dash-hero { padding: 32px 24px 22px; }
  }
  @media (max-width: 768px) {
    .dash-hero { flex-direction: column; align-items: flex-start; padding: 24px 20px 18px; }
    .dash-hero-actions { width: 100%; }
    .dash-row { padding: 0 20px; }
  }
  @media (max-width: 450px) {
    .dash-hero { padding: 20px 14px 16px; }
    /* Two nowrap btn-lg overflow at 360px; let them wrap and share the row. */
    .dash-hero-actions { flex-wrap: wrap; }
    .dash-hero-actions .btn { flex: 1 1 auto; justify-content: center; }
    .dash-row { padding: 0 10px; }
    .card { padding: 16px 14px; }
    .card-head { gap: 10px; }
    .card-h { font-size: 20px; }
    /* Global 44px touch rule turns the 20px checkbox and inline text links into tall slabs. */
    .btn-sm { min-height: 36px; }
    .todo-check, .todo-del, .link-btn { min-height: 0; }
    .job-row { gap: 10px; }
    .todo-row { gap: 8px; grid-template-columns: 22px 24px minmax(0, 1fr) auto auto; padding: 10px 4px; }
    .todo-icon { width: 24px; height: 24px; }
    .news-row { gap: 12px; }
    /* Four stages + three arrows leave ~44px per stage; drop the arrows so labels fit. */
    .pipe-arrow { display: none; }
    .pipe-stage { padding: 8px 2px; }
    .pipe-count { font-size: 28px; }
    .pipe-label { font-size: 11.5px; text-align: center; }
    .dash-modal-backdrop { padding: 12px; }
    .dash-modal-head, .dash-modal-body, .dash-modal-foot { padding-left: 16px; padding-right: 16px; }
  }
`;
